import React, { useState } from 'react';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import styled from 'styled-components';
import Typography from '@mui/material/Typography';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import IconButton from '@mui/material/IconButton';
import { ScrapedContentPreview } from '../ScrapedContentPreview';
import { Link } from '../../app/feathers/shared/Link';
import { ScrapedContent } from '../../app/feathers/scraped-content/scraped-content';
import { getSourceUsageCount } from '../../app/util';
import { Source } from '../../app/feathers/shared/Source';
import { areUrlsEquivalent, countEquivalentHostnames } from '../../util/sanitize-and-classify-links';

interface EditableReferenceProps {
  value?: Link;
  onChange?: (link: Link) => void;
  onDelete?: () => void;
  scrapedContent?: ScrapedContent[];
  onScrapedContentSaved?: (scrapedContent: ScrapedContent) => void;
  editingEnabled?: boolean;
  showSourceMetadata?: boolean;
  style?: React.CSSProperties;
  sourcesUsedInProfile?: Source[];
  compareUsageByHostname?: boolean;
}

const EditableReference: React.FC<EditableReferenceProps> = ({
  value,
  sourcesUsedInProfile = [],
  onChange,
  onDelete,
  scrapedContent = [],
  onScrapedContentSaved,
  editingEnabled = true,
  showSourceMetadata = true,
  compareUsageByHostname = false,
  style
}) => {
  const [editing, setEditing] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [url, setUrl] = useState(value?.url || '');
  const [title, setTitle] = useState(value?.title || '');

  // Update local state when value prop changes
  React.useEffect(() => {
    setUrl(value?.url || '');
    setTitle(value?.title || '');
  }, [value?.url, value?.title]);

  const dualEdit = value?.mediaType === 'other';

  let displayNameOfSource: string;
  if(value?.mediaType === 'website') displayNameOfSource = value?.url || '';
  else if(value?.mediaType === 'questionnaire') displayNameOfSource = value.title || 'Questionnaire';
  else if(value?.title) displayNameOfSource = value.title;
  else displayNameOfSource = 'Other source';

  const linkUsedNTimes = value?.url 
    ? compareUsageByHostname
      ? countEquivalentHostnames(value.url, sourcesUsedInProfile)
      : getSourceUsageCount(value.url, value.mediaType || 'other', sourcesUsedInProfile)
    : 0;

  let scrapedContentForSource: ScrapedContent | null = null;
  if(Array.isArray(scrapedContent)) {
    const foundItem = scrapedContent.find(sc => {
      const linkUrl = value?.url;
      if(!linkUrl) return false;
      return sc?.url && areUrlsEquivalent(sc.url, linkUrl);
    })
    scrapedContentForSource = foundItem ?? null;
  }

  const handleSave = () => {
    // If URL hasn't changed (and title hasn't changed if dualEdit), treat as cancel
    const urlUnchanged = url === (value?.url || '');
    const titleUnchanged = !dualEdit || title === (value?.title || '');
    
    if (urlUnchanged && titleUnchanged) {
      handleCancel();
      return;
    }

    if (onChange && value) {
      onChange({
        ...value,
        url,
        ...(dualEdit ? { title } : {})
      });
    }
    setEditing(false);
  };

  const handleCancel = () => {
    setEditing(false);
    setUrl(value?.url || '');
    setTitle(value?.title || '');
  };

  if(editing) {
    return (
      <Row style={style || {}}>
        <div
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          }}
        >
          {dualEdit && (
            <TextField
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTitle(e.target.value)}
              value={title}
              name={'title'}
              placeholder='Article Title'
              size='small'
              fullWidth
            />
          )}
          <TextField
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUrl(e.target.value)}
            value={url}
            name={'url'}
            placeholder='URL (https://example.com)'
            size='small'
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size='small'
                    onClick={handleSave}
                    style={{ padding: '4px' }}
                  >
                    <CheckIcon style={{ fontSize: '18px' }}/>
                  </IconButton>
                  <IconButton
                    size='small'
                    onClick={handleCancel}
                    style={{ padding: '4px' }}
                  >
                    <CancelIcon style={{ fontSize: '18px' }}/>
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        </div>
      </Row>
    );
  } else {
    return (
      <Row 
        style={style || {}}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <LinkWrapper>
          <LinkContent>
            {
              value?.url
              ? (
                  <a 
                    href={value?.url} 
                    target={'_blank'} 
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    style={{ color: 'inherit' }}
                  >
                    <Typography variant={'body1'}>{displayNameOfSource}</Typography>
                  </a>
                )
              : <Typography variant={'body1'} style={{ opacity: 0.6 }}>(missing url)</Typography>
            }
          </LinkContent>
          {editingEnabled && isHovered && (
            <IconButtonsContainer>
              {onChange && (
                <IconButton
                  size='small'
                  className={'icon-button'}
                  onClick={() => setEditing(true)}
                  style={{ padding: '4px' }}
                >
                  <EditIcon style={{ fontSize: '18px' }}/>
                </IconButton>
              )}
              {onDelete && (
                <IconButton
                  size='small'
                  className={'icon-button'}
                  onClick={onDelete}
                  style={{ padding: '4px' }}
                >
                  <CancelIcon style={{ fontSize: '18px' }}/>
                </IconButton>
              )}
            </IconButtonsContainer>
          )}
        </LinkWrapper>
        {showSourceMetadata && (
          <div style={{ display: 'flex', gap: '4px', alignItems: 'center', marginTop: '-8px'}}>
            <ScrapedContentPreview
              scrapedContent={scrapedContentForSource}
              onScrapedContentSaved={onScrapedContentSaved}
              editingEnabled={editingEnabled}
            />
            <Typography variant='subtitle1' style={{ fontSize: '14px' }}>·</Typography>
            <UsedNTimes n={linkUsedNTimes} />
          </div>
        )}
      </Row>
    )
  }
}

const UsedNTimes: React.FC<{ n: number }> = ({ n }) => {
  return <Typography variant={'subtitle1'} style={{ opacity: 0.6, fontSize: '14px' }}>Used {n} times</Typography>
}

export default EditableReference;

const Row = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: calc(100%);
`

const LinkWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  min-height: 28px;
`

const LinkContent = styled.div`
  flex: 1;
  margin-top: -4px;
  
  a {
    display: inline-block;
    cursor: pointer;
  }
`

const IconButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  margin-left: 8px;
`
